import React, { useState } from "react";
import {
  FaUser,
  FaBuilding,
  FaPhone,
  FaEnvelope,
  FaLaptop,
  FaMobileAlt,
  FaTabletAlt,
  FaSitemap,
  FaUserTie,
  FaCalendarAlt,
  FaIdCard,
  FaEdit,
  FaSave,
  FaTimes,
} from "react-icons/fa";
import { Modal } from "./common";
import axiosInstance from "../utils/axiosConfig";

function EmployeeModal({ employee, onClose }) {
  const [isEditingDetalles, setIsEditingDetalles] = useState(false);
  const [detallesCargo, setDetallesCargo] = useState(employee?.detalles_cargo || "");
  const [isSaving, setIsSaving] = useState(false);

  // Función para formatear fechas
  const formatearFecha = (fechaStr) => {
    if (!fechaStr) return 'No registrada';
    try {
      const fecha = new Date(fechaStr);
      const dia = String(fecha.getDate()).padStart(2, '0');
      const mes = String(fecha.getMonth() + 1).padStart(2, '0');
      const año = fecha.getFullYear();
      return `${dia}/${mes}/${año}`;
    } catch (error) {
      return fechaStr;
    }
  };

  // Función para guardar detalles de cargo
  const handleSaveDetalles = async () => {
    setIsSaving(true);
    try {
      const response = await axiosInstance.patch(`/api/empleados/${employee.id}`, {
        detalles_cargo: detallesCargo
      });

      if (response.status < 200 || response.status >= 300) {
        throw new Error('Error al actualizar detalles de cargo');
      }

      // Actualizar el employee local
      employee.detalles_cargo = detallesCargo;
      setIsEditingDetalles(false);
    } catch (error) {
      console.error('Error:', error);
      alert('Error al guardar los detalles del cargo');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setDetallesCargo(employee.detalles_cargo || "");
    setIsEditingDetalles(false);
  };

  // Componente reutilizable para los items del grid
  const DetailItem = ({ icon: Icon, label, children, className = "" }) => (
    <div className={`flex items-start gap-3 p-4 bg-gray-50 rounded-xl ${className}`}>
      <Icon className="text-sura-blue text-lg mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <strong className="block mb-1 text-gray-500 text-xs font-semibold uppercase tracking-wider">
          {label}
        </strong>
        {children}
      </div>
    </div>
  );

  return (
    <Modal isOpen={!!employee} onClose={onClose} size="xl">
      <Modal.Header onClose={onClose}>
        Detalle del Empleado
      </Modal.Header>
      <Modal.Body className="p-6">
        {/* ============================================
            HEADER: Avatar + Info Personal + Ficha/Cargo
            ============================================ */}
        <div className="flex flex-col sm:flex-row gap-6 items-start pb-6 border-b-2 border-gray-100 mb-6">
          {/* Avatar */}
          <div className="w-28 h-28 rounded-xl overflow-hidden flex-shrink-0 shadow-md bg-gradient-to-br from-blue-50 to-gray-100">
            {employee.foto ? (
              <img src={employee.foto} alt={employee.nombre} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <FaUser className="text-5xl text-sura-blue" />
              </div>
            )}
          </div>

          {/* Info Personal */}
          <div className="flex-1">
            <h2 className="m-0 text-sura-blue text-2xl font-bold mb-2">{employee.nombre}</h2>
            <div className="flex flex-col gap-1.5">
              <p className="m-0 flex items-center gap-2 text-gray-600 text-sm">
                <FaCalendarAlt className="text-blue-500" />
                <strong className="text-gray-700 font-medium">Fecha de Nacimiento:</strong>
                {formatearFecha(employee.fecha_nacimiento)}
              </p>
              <p className="m-0 flex items-center gap-2 text-gray-600 text-sm">
                <FaIdCard className="text-blue-500" />
                <strong className="text-gray-700 font-medium">Cédula:</strong>
                {employee.cedula || 'No registrada'}
              </p>
            </div>
          </div>

          {/* Ficha y Cargo */}
          <div className="flex flex-col items-end gap-3 text-right">
            <span className="inline-block bg-sura-blue text-white px-4 py-2 rounded-lg text-sm font-bold shadow-sm">
              Ficha: {employee.ficha}
            </span>
            <p className="text-sura-blue m-0 text-base font-semibold">{employee.cargo}</p>
          </div>
        </div>

        {/* ============================================
            DETAILS GRID - 3 columnas
            ============================================ */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Row 1 */}
          <DetailItem icon={FaBuilding} label="Sede">
            <p className="m-0 text-gray-800 text-sm font-medium">{employee.sede}</p>
          </DetailItem>

          <DetailItem icon={FaSitemap} label="Organización">
            <div className="flex flex-col gap-0.5">
              <p className="m-0 font-semibold text-sura-blue">{employee.gerencia}</p>
              <p className="m-0 pl-3 text-gray-700 text-sm">↳ {employee.departamento}</p>
              {employee.area && <p className="m-0 pl-6 text-gray-500 text-sm">↳ {employee.area}</p>}
            </div>
          </DetailItem>

          <DetailItem icon={FaUserTie} label="Supervisor">
            <p className="m-0 text-gray-800 text-sm font-medium">{employee.supervisor || 'Sin supervisor'}</p>
          </DetailItem>

          {/* Row 2 */}
          <DetailItem icon={FaCalendarAlt} label="Fecha de Ingreso">
            <p className="m-0 text-gray-800 text-sm font-medium">{formatearFecha(employee.fecha_ingreso)}</p>
          </DetailItem>

          <DetailItem icon={FaEdit} label="Detalles del Cargo">
            {isEditingDetalles ? (
              <div className="flex flex-col gap-2 w-full">
                <input
                  type="text"
                  value={detallesCargo}
                  onChange={(e) => setDetallesCargo(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  placeholder="Detalles del cargo..."
                  disabled={isSaving}
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveDetalles}
                    className="px-3 py-1.5 text-xs rounded-lg bg-green-600 text-white font-medium flex items-center gap-1"
                    disabled={isSaving}
                  >
                    <FaSave /> {isSaving ? 'Guardando...' : 'Guardar'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-3 py-1.5 text-xs rounded-lg bg-gray-200 text-gray-700 font-medium flex items-center gap-1"
                    disabled={isSaving}
                  >
                    <FaTimes /> Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 flex-wrap">
                <p className="m-0 text-gray-800 text-sm">{detallesCargo || 'Sin detalles'}</p>
                <button
                  onClick={() => setIsEditingDetalles(true)}
                  className="bg-white border border-gray-300 text-gray-600 px-2 py-1 rounded-lg text-xs cursor-pointer flex items-center gap-1 hover:bg-gray-100"
                >
                  <FaEdit /> Editar
                </button>
              </div>
            )}
          </DetailItem>

          <DetailItem icon={FaEnvelope} label="Correo">
            <p className="m-0 text-gray-800 text-sm font-medium break-all">{employee.correo || "N/A"}</p>
          </DetailItem>

          {/* Row 3 */}
          <DetailItem icon={FaPhone} label="Extensión">
            <p className="m-0 font-mono text-xl font-bold text-sura-blue bg-blue-50 px-3 py-1 rounded-lg inline-block">
              {employee.extension || "N/A"}
            </p>
          </DetailItem>

          <DetailItem icon={FaLaptop} label="Equipo Asignado">
            {employee.computadora_asignada ? (
              <div>
                <span className="font-mono text-base font-bold text-green-700 bg-green-50 px-2.5 py-1 rounded-lg inline-block">
                  {employee.computadora_asignada.activo_fijo}
                </span>
                <span className="text-gray-500 text-xs block mt-1">
                  {employee.computadora_asignada.marca} {employee.computadora_asignada.modelo}
                </span>
              </div>
            ) : (
              <p className="m-0 text-gray-500 text-sm">No hay equipo asignado</p>
            )}
          </DetailItem>

          <DetailItem icon={FaMobileAlt} label="Smartphone Asignado">
            {employee.telefono_asignado ? (
              <div>
                <span className="font-mono text-base font-bold text-green-700 bg-green-50 px-2.5 py-1 rounded-lg inline-block">
                  {employee.telefono_asignado.activo_fijo}
                </span>
                <span className="text-gray-500 text-xs block mt-1">
                  {employee.telefono_asignado.marca} {employee.telefono_asignado.modelo}
                </span>
              </div>
            ) : (
              <p className="m-0 text-gray-500 text-sm">No hay smartphone asignado</p>
            )}
          </DetailItem>

          {/* Row 4 */}
          <DetailItem icon={FaTabletAlt} label="Tablet Asignada">
            {employee.tablet_asignada ? (
              <div>
                <span className="font-mono text-base font-bold text-green-700 bg-green-50 px-2.5 py-1 rounded-lg inline-block">
                  {employee.tablet_asignada.activo_fijo}
                </span>
                <span className="text-gray-500 text-xs block mt-1">
                  {employee.tablet_asignada.marca} {employee.tablet_asignada.modelo}
                </span>
              </div>
            ) : (
              <p className="m-0 text-gray-500 text-sm">No hay tablet asignada</p>
            )}
          </DetailItem>

          {employee.linea && (
            <DetailItem icon={FaPhone} label="Línea Telefónica">
              <p className="m-0 font-mono text-base font-medium text-gray-800">{employee.linea}</p>
            </DetailItem>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default EmployeeModal;
